import { apiFetch } from "../../../context/AuthContext";

const API_BASE_URL = process.env.REACT_APP_BASE_URL || "https://nexploy-eg.com";

/**
 * Employee Actions API Service - ENHANCED WITH WORKFLOW & HISTORY
 * Handles all API calls for employee actions management
 */

class EmployeeActionsAPI {
  constructor(baseURL = API_BASE_URL) {
    this.baseURL = baseURL;
  }

  /**
   * Generic fetch wrapper with error handling
   */
  async fetchWithAuth(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const defaultHeaders = {};
    // Only set Content-Type for JSON bodies (avoid for FormData/uploads)
    const hasBody = options && options.body !== undefined && options.body !== null;
    const isFormData = typeof FormData !== "undefined" && hasBody && options.body instanceof FormData;
    if (hasBody && !isFormData) {
      defaultHeaders["Content-Type"] = "application/json";
      // Ensure body is a string for JSON requests
      if (typeof options.body !== "string") {
        options = { ...options, body: JSON.stringify(options.body) };
      }
    }

    try {
      console.log(`🔵 API Request: ${options.method || "GET"} ${url}`);
      if (options.body) {
        console.log("📦 Request Body:", JSON.parse(options.body));
      }

      const response = await apiFetch(url, {
        ...options,
        // Every call through this class goes straight to the server —
        // never served out of the browser's HTTP cache. Without this,
        // a GET made right after a create/approve/reject can come back
        // from cache with the pre-change data, so the UI looks like the
        // change "didn't appear" even though the refetch call fired.
        cache: "no-store",
        headers: {
          ...defaultHeaders,
          ...(options.headers || {}),
        },
      });

      // Handle non-JSON responses
      const contentType = response.headers.get("content-type");
      let data;

      if (contentType && contentType.includes("application/json")) {
        data = await response.json();
      } else {
        const text = await response.text();
        data = { message: text };
      }

      if (!response.ok) {
        console.error(`🔴 API Error: ${response.status}`, data);
        const msg = (data && (data.message || data.error)) || `HTTP error! status: ${response.status}`;
        throw new Error(msg);
      }

      console.log(`🟢 API Success:`, data);
      return data;
    } catch (error) {
      console.error(`🔴 API Error [${endpoint}]:`, error);
      throw error;
    }
  }

  // ==================== EMPLOYEE ENDPOINTS ====================

  async getEmployees(filters = {}) {
    const queryParams = new URLSearchParams();

    if (filters.clientIdentifier) {
      const endpoint = `/employees/clients/${filters.clientIdentifier}`;
      const response = await this.fetchWithAuth(endpoint);
      return Array.isArray(response) ? response : response.employees || [];
    }

    if (filters.department && filters.department !== "all") {
      queryParams.append("department", filters.department);
    }
    if (filters.performance && filters.performance !== "all") {
      queryParams.append("performance", filters.performance);
    }
    if (filters.searchTerm) {
      queryParams.append("search", filters.searchTerm);
    }

    const queryString = queryParams.toString();
    const endpoint = queryString ? `/employees?${queryString}` : "/employees";

    const response = await this.fetchWithAuth(endpoint);
    return Array.isArray(response) ? response : response.data || [];
  }

  async getAllClients() {
    const response = await this.fetchWithAuth("/employees/clients/all");
    return response.clients || response.data || response || [];
  }



  // In your employeeActionsAPI.js (document 3)

  async getEmployeesByClient(clientIdentifier) {
    if (!clientIdentifier) {
      throw new Error("Client identifier is required");
    }

    console.log(`🔵 Fetching employees for client: ${clientIdentifier}`);

    const response = await this.fetchWithAuth(
      `/employees/clients/${clientIdentifier}`
    );

    console.log(`🟢 Received response:`, response);

    // Handle different response formats
    if (Array.isArray(response)) {
      return response;
    }

    if (response.employees) {
      return response.employees;
    }

    if (response.data) {
      return Array.isArray(response.data) ? response.data : response.data.employees || [];
    }

    return [];
  }

  async getEmployeesByClientbymanager(clientIdentifier, myInfinityCode, viewType = null) {
    if (!clientIdentifier) {
      throw new Error("Client identifier is required");
    }

    // Use provided viewType, fall back to localStorage for backwards-compatibility
    const resolvedViewType = viewType || localStorage.getItem('managerViewType') || 'manager';

    console.log(`🔵 Fetching employees for client: ${clientIdentifier}/Admin1?managerInfinityCode=${myInfinityCode}&viewType=${resolvedViewType}`);

    const response = await this.fetchWithAuth(
      `/employees/clients/${clientIdentifier}/Admin1?managerInfinityCode=${myInfinityCode}&viewType=${resolvedViewType}`
    );

    console.log(`🟢 Received response:`, response);

    // Handle different response formats
    if (Array.isArray(response)) {
      return response;
    }

    if (response.employees) {
      return response.employees;
    }

    if (response.data) {
      return Array.isArray(response.data) ? response.data : response.data.employees || [];
    }

    return [];
  }

  /**
   * Get employees visible to the current user according to admin-level visibility rules
   * Server uses the authenticated user (from token) to resolve visibility. Optionally pass `clientName`.
   */
  async getEmployeesByVisibility(clientName, viewType) {
    const query = clientName ? `?clientName=${encodeURIComponent(clientName)}` : "";
    const options = viewType
      ? { headers: { "x-view-type": viewType } }
      : {};
    const response = await this.fetchWithAuth(`/employees/visibility${query}`, options);

    // Response format: { success: true, count, client, employees }
    if (!response) return [];
    if (Array.isArray(response)) return response;
    if (response.employees) return response.employees;
    if (response.data && Array.isArray(response.data)) return response.data;
    if (response.data && Array.isArray(response.data.employees)) return response.data.employees;
    return [];
  }

  /**
   * Get employees by admin level (uses payroll visibility endpoint)
   * Accepts an adminLevelId (or admin infinityCode) and optional workingCompany to further scope results.
   */
  async getEmployeesByAdminLevel(adminLevelId, workingCompany) {
    if (!adminLevelId) throw new Error("adminLevelId is required");
    const q = workingCompany ? `?workingCompany=${encodeURIComponent(workingCompany)}` : "";
    // Use payrollX mount used elsewhere in frontend
    const endpoint = `/payrollX/visibility/admin/${encodeURIComponent(adminLevelId)}/employees${q}`;
    const response = await this.fetchWithAuth(endpoint);

    if (!response) return [];
    if (Array.isArray(response)) return response;
    if (response.data && Array.isArray(response.data)) return response.data;
    if (response.data && Array.isArray(response.data.data)) return response.data.data;
    if (response.data && response.data.employees) return response.data.employees;
    if (response.employees) return response.employees;
    return [];
  }

  async getEmployeeByInfinityCode(infinityCode) {
    const response = await this.fetchWithAuth(
      `/employees/select/${infinityCode}`
    );
    return response.employee || response.data || response;
  }

  async getActiveEmployeeCount(clientName) {
    const response = await this.fetchWithAuth(`/employees/client/${clientName}/Active`);
    if (typeof response === "number") return response;
    if (response.count !== undefined) return response.count;
    if (response.data !== undefined) return response.data;
    return 0;
  }

  async getEmployeeCount(clientname) {
    const response = await this.fetchWithAuth(`/employees/client/${clientname}/count`);
    if (typeof response === "number") return response;
    if (response.count !== undefined) return response.count;
    if (response.data !== undefined) return response.data;
    return 0;
  }

  // ==================== DEPARTMENT ENDPOINTS ====================

  /**
   * List departments for a client/company from the departments API — the
   * same table the Departments admin page reads and writes. This is the
   * one place action forms and employee dropdowns should get department
   * names from, instead of each screen re-deriving its own list from
   * whatever employees happen to be loaded.
   * @param {string} clientName
   * @param {string} [workingCompany]
   * @param {boolean} [includeInactive]
   */
  async getDepartments(clientName, workingCompany, includeInactive = false) {
    if (!clientName) throw new Error("clientName is required");
    const params = new URLSearchParams({ clientName });
    if (workingCompany) params.append("workingCompany", workingCompany);
    if (includeInactive) params.append("includeInactive", "true");

    const response = await this.fetchWithAuth(`/api/departments?${params}`);
    return Array.isArray(response) ? response : response.data || [];
  }

  // ==================== ACTION CRUD ENDPOINTS ====================

  async createAction(actionData) {
    console.log("📝 Creating action with data:", actionData);

    if (!actionData.infinityCode) throw new Error("infinityCode is required");
    if (!actionData.actionType) throw new Error("actionType is required");
    if (!actionData.effectiveDate) throw new Error("effectiveDate is required");
    if (!actionData.reason) throw new Error("reason is required");

    const response = await this.fetchWithAuth("/employee-actions", {
      method: "POST",
      body: JSON.stringify(actionData),
    });

    return response.data || response;
  }

  async getAllActions(filters = {}) {
    const queryParams = new URLSearchParams();

    if (filters.status) queryParams.append("status", filters.status);
    if (filters.actionType)
      queryParams.append("actionType", filters.actionType);
    if (filters.department)
      queryParams.append("department", filters.department);
    if (filters.startDate) queryParams.append("startDate", filters.startDate);
    if (filters.endDate) queryParams.append("endDate", filters.endDate);
    if (filters.searchTerm)
      queryParams.append("searchTerm", filters.searchTerm);

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `/employee-actions?${queryString}`
      : "/employee-actions";

    const response = await this.fetchWithAuth(endpoint);
    return Array.isArray(response) ? response : response.data || [];
  }

  async getActionById(id) {
    const response = await this.fetchWithAuth(`/employee-actions/${id}`);
    return response.data || response;
  }

  async getEmployeeActions(infinityCode) {
    const response = await this.fetchWithAuth(
      `/employee-actions/employee/${infinityCode}`
    );
    return Array.isArray(response) ? response : response.data || [];
  }

  async updateAction(id, updateData) {
    const response = await this.fetchWithAuth(`/employee-actions/${id}`, {
      method: "PUT",
      body: JSON.stringify(updateData),
    });
    return response.data || response;
  }

  async deleteAction(id) {
    const response = await this.fetchWithAuth(`/employee-actions/${id}`, {
      method: "DELETE",
    });
    return response.data || response;
  }

  // ==================== WORKFLOW ENDPOINTS ====================

  async approveAction(id, approvalData) {
    const response = await this.fetchWithAuth(
      `/employee-actions/${id}/approve`,
      {
        method: "PUT",
        body: JSON.stringify(approvalData),
      }
    );
    return response.data || response;
  }

  async rejectAction(id, rejectionData) {
    const response = await this.fetchWithAuth(
      `/employee-actions/${id}/reject`,
      {
        method: "PUT",
        body: JSON.stringify(rejectionData),
      }
    );
    return response.data || response;
  }

  async processAction(id, processorData) {
    const response = await this.fetchWithAuth(
      `/employee-actions/${id}/process`,
      {
        method: "PUT",
        body: JSON.stringify(processorData),
      }
    );
    return response.data || response;
  }

  async bulkApproveActions(actionIds, approvedBy, comments) {
    const response = await this.fetchWithAuth(
      "/employee-actions/bulk/approve",
      {
        method: "POST",
        body: JSON.stringify({
          actionIds,
          approvedBy,
          comments,
        }),
      }
    );
    return response.data || response;
  }

  // ==================== NEW: WORKFLOW-SPECIFIC ENDPOINTS ====================

  async getActionWorkflow(id) {
    const response = await this.fetchWithAuth(
      `/employee-actions/${id}/workflow`
    );
    return Array.isArray(response) ? response : response.data || [];
  }

  /**
   * Approve a specific workflow step (Revision 3 engine).
   * @param {number} workflowInstanceId
   * @param {number} stepOrder
   * @param {string} approvedBy   — infinityCode of the approver
   * @param {string} [comments]
   */
  async approveWorkflowStep(workflowInstanceId, stepOrder, approvedBy, comments = "") {
    const response = await this.fetchWithAuth(
      `/workflows/${workflowInstanceId}/steps/${stepOrder}/approve`,
      {
        method: "PUT",
        body: JSON.stringify({ approvedBy, comments }),
      }
    );
    return response.data || response;
  }

  /**
   * Reject a specific workflow step (Revision 3 engine).
   */
  async rejectWorkflowStep(workflowInstanceId, stepOrder, rejectedBy, comments = "") {
    const response = await this.fetchWithAuth(
      `/workflows/${workflowInstanceId}/steps/${stepOrder}/reject`,
      {
        method: "PUT",
        body: JSON.stringify({ rejectedBy, comments }),
      }
    );
    return response.data || response;
  }

  /**
   * Fetch all pending approval steps for the current user
   * across ALL modules (unified inbox).
   * @param {string}   infinityCode
   * @param {number}   [adminLevelId]
   * @param {string}   [module]  — optional filter e.g. "ACTION"
   */
  async getPendingApprovals(infinityCode, adminLevelId, module) {
    const params = new URLSearchParams({ infinityCode });
    if (adminLevelId) params.append("adminLevelId", adminLevelId);
    if (module) params.append("module", module);
    const response = await this.fetchWithAuth(`/workflows/pending?${params}`);
    return Array.isArray(response) ? response : response.data || [];
  }

  /**
   * List workflow definitions for the Configuration → Workflows tab.
   * @param {string} clientName
   * @param {string} workingCompany
   */
  async listWorkflowDefinitions(clientName, workingCompany) {
    const params = new URLSearchParams({ clientName, workingCompany });
    const response = await this.fetchWithAuth(`/workflows/definitions?${params}`);
    return Array.isArray(response) ? response : response.data || [];
  }

  /**
   * List company employees selectable as explicit workflow assignees.
   * @param {string} clientName
   * @param {string} workingCompany
   */
  async listWorkflowAssignableEmployees(clientName, workingCompany) {
    const params = new URLSearchParams({ clientName, workingCompany });
    const response = await this.fetchWithAuth(`/workflows/assignable-employees?${params}`);
    return Array.isArray(response) ? response : response.data || [];
  }

  /**
   * Save / update a workflow definition from the Configuration → Workflows tab.
   * @param {object} definition  — { clientName, workingCompany, module, subType, targetEmployeeType, steps[] }
   */
  async saveWorkflowDefinition(definition) {
    const response = await this.fetchWithAuth("/workflows/definitions", {
      method: "POST",
      body: JSON.stringify(definition),
    });
    return response.data || response;
  }

  /**
   * Seed the default ACTION/ADVANCEMENT/DOCUMENT/LEAVE workflow definitions
   * for a company that has zero WorkflowDefinition rows yet. No-op
   * (returns { created: false, ... }) if the company already has definitions.
   * @param {string} clientName
   * @param {string} workingCompany
   */
  async ensureDefaultWorkflowDefinitions(clientName, workingCompany) {
    const response = await this.fetchWithAuth("/workflows/definitions/ensure-defaults", {
      method: "POST",
      body: JSON.stringify({ clientName, workingCompany }),
    });
    return response.data || response;
  }

  async getActionTimeline(id) {
    const response = await this.fetchWithAuth(
      `/employee-actions/${id}/timeline`
    );
    return response.data || response;
  }

  async getActionsByWorkflowStep(step) {
    const response = await this.fetchWithAuth(
      `/employee-actions/workflow/pending/${step}`
    );
    return Array.isArray(response) ? response : response.data || [];
  }

  // ==================== HISTORY ENDPOINTS ====================

  async getActionHistory(infinityCode) {
    const response = await this.fetchWithAuth(
      `/employee-actions/history/${infinityCode}`
    );
    return Array.isArray(response) ? response : response.data || [];
  }

  async getEmployeeChangeSummary(infinityCode) {
    const response = await this.fetchWithAuth(
      `/employee-actions/employee/${infinityCode}/summary`
    );
    return response.data || response;
  }

  // ==================== STATISTICS ENDPOINTS ====================

  async getActionStats(filters = {}) {
    const queryParams = new URLSearchParams();

    if (filters.startDate) queryParams.append("startDate", filters.startDate);
    if (filters.endDate) queryParams.append("endDate", filters.endDate);

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `/employee-actions/stats?${queryString}`
      : "/employee-actions/stats";

    const response = await this.fetchWithAuth(endpoint);
    return response.data || response;
  }

  // ==================== MANAGER ENDPOINTS ====================

  async getManagerPendingActions(infinityCode) {
    const response = await this.fetchWithAuth(
      `/employee-actions/manager/${infinityCode}/pending`
    );
    return Array.isArray(response) ? response : response.data || [];
  }

  // ==================== NEW: PAYROLL IMPACT ENDPOINTS ====================

  async getPayrollImpactPreview(
    infinityCode,
    effectiveDate,
    newSalary,
    oldSalary
  ) {
    const response = await this.fetchWithAuth(
      "/employee-actions/payroll-impact-preview",
      {
        method: "POST",
        body: JSON.stringify({
          infinityCode,
          effectiveDate,
          newSalary,
          oldSalary,
        }),
      }
    );
    return response.data || response;
  }

  /**
   * Fetch pending actions requested by a specific requester (you).
   * If `requestedBy` is not provided, attempt to read `infinityCode` from localStorage
   * or decode it from the stored JWT token payload.
   */
  async getPendingActions(requestedBy, infinityCodeFallback = null) {
    // Try provided param first
    let requester = requestedBy || infinityCodeFallback || localStorage.getItem("infinityCode");

    // If not available, try to decode token payload to extract an identifier
    if (!requester) {
      const token = localStorage.getItem("token");
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split(".")[1]));
          requester = payload.infinityCode || payload.sub || payload.userId;
        } catch (e) {
          // ignore decoding errors
        }
      }
    }

    // Build query: filter pending actions and, if we have a requester, restrict to that requester
    const queryParams = new URLSearchParams();
    queryParams.append("status", "pending");
    if (requester) queryParams.append("requestedBy", requester);

    const endpoint = `/employee-actions?${queryParams.toString()}`;

    const response = await this.fetchWithAuth(endpoint);
    return Array.isArray(response) ? response : response.data || response;
  }

  /**
   * Get pending actions filtered according to the current view.
   * - viewRole: 'admin' | 'manager'
   * - options: { adminLevelId, clientIdentifier, clientName, managerInfinityCode, workingCompany }
   *
   * Strategy: fetch pending actions, fetch employees relevant to the view (using existing methods),
   * then return actions where the action's employee Infinity Code exists in the employee set.
   */
  async getPendingActionsByView(viewRole = "admin", options = {}) {
    // fetch pending actions first
    // For admin view we must fetch ALL pending actions (no requestedBy filter)
    let pendingResp;
    try {
      pendingResp = await this.fetchWithAuth(`/employee-actions?status=pending`);
    } catch (e) {
      // fallback to generic getter
      pendingResp = await this.getPendingActions();
    }

    // Normalize pendingResp into an array of actions regardless of response shape
    const normalizeActions = (resp) => {
      if (!resp) return [];
      if (Array.isArray(resp)) return resp;
      if (Array.isArray(resp.data)) return resp.data;
      if (Array.isArray(resp.actions)) return resp.actions;
      if (Array.isArray(resp.items)) return resp.items;
      if (resp.data && Array.isArray(resp.data.actions)) return resp.data.actions;
      if (resp.data && Array.isArray(resp.data.data)) return resp.data.data;
      // try to find arrays on common keys
      for (const key of ["employees", "results", "list"]) {
        if (Array.isArray(resp[key])) return resp[key];
        if (resp.data && Array.isArray(resp.data[key])) return resp.data[key];
      }
      return [];
    };

    const actions = normalizeActions(pendingResp);

    // determine employee list based on viewRole
    let employees = [];

    if (viewRole === "admin") {
      if (options.adminLevelId) {
        employees = await this.getEmployeesByAdminLevel(
          options.adminLevelId,
          options.workingCompany
        );
      } else if (options.clientIdentifier) {
        employees = await this.getEmployeesByClient(options.clientIdentifier);
      } else {
        const company = options.workingCompany;
        employees = company
          ? await this.getEmployeesByVisibility(company, "admin")
          : await this.getEmployeesByVisibility(undefined, "admin");
      }

      // If we couldn't resolve any employees for admin view, return all pending actions
      if (!employees || employees.length === 0) {
        return actions || [];
      }
    } else if (viewRole === "manager") {
      // options.managerInfinityCode should be set by the caller from profile data
      let managerCode =
        options.managerInfinityCode || localStorage.getItem("infinityCode");

      if (!managerCode) {
        const token = localStorage.getItem("token");
        if (token) {
          try {
            const payload = JSON.parse(atob(token.split(".")[1]));
            managerCode = payload.infinityCode || payload.sub || payload.userId;
          } catch (e) {
            // ignore
          }
        }
      }

      if (options.clientIdentifier && managerCode) {
        employees = await this.getEmployeesByClientbymanager(
          options.clientIdentifier,
          managerCode
        );
      } else {
        // fallback: fetch visibility and filter by managerCode (first-line)
        const company = options.workingCompany;
        const all = company
          ? await this.getEmployeesByVisibility(company, "admin")
          : await this.getEmployeesByVisibility(undefined, "admin");
        employees = (all || []).filter((e) => {
          if (!e) return false;
          const mCode = e.managerInfinityCode || e.directManager || e.lineManager || e.manager || e.managerId;
          if (mCode !== managerCode) return false;
          const firstLineMarker =
            e.managerType === "firstline" || e.lineManagerType === "firstline" || e.reportingLevel === 1 || e.reportingLevel === "1";
          return firstLineMarker || (!("managerType" in e) && !("lineManagerType" in e) && !("reportingLevel" in e));
        });
      }

      // Prefer the dedicated manager inbox when available because workflow-based
      // approvals are assigned by workflow step assignee, not by the employee-list filter.
      if (managerCode) {
        try {
          const mgrPending = await this.getManagerPendingActions(managerCode);
          const normalizedMgrPending = Array.isArray(mgrPending)
            ? mgrPending
            : mgrPending?.data || [];
          if (Array.isArray(normalizedMgrPending) && normalizedMgrPending.length > 0) {
            return normalizedMgrPending;
          }
        } catch (err) {
          // ignore and continue to filtering (will return empty)
        }
      }
    } else {
      // unknown role, return empty
      return [];
    }

    const normalizeCode = (val) => {
      if (val === null || val === undefined) return null;
      return String(val).trim().toLowerCase();
    };

    const empSet = new Set(
      (employees || [])
        .map((e) => normalizeCode(e.infinityCode || e.InfinityCode || e.infinity || e.code || e.id))
        .filter(Boolean)
    );

    const filtered = (actions || []).filter((action) => {
      const raw =
        action.infinityCode ||
        action.employeeInfinityCode ||
        (action.employee && (action.employee.infinityCode || action.employee.infinity || action.employee.id)) ||
        action.employeeId ||
        action.employee;
      const aCode = normalizeCode(raw);
      return !!aCode && empSet.has(aCode);
    });

    return filtered;
  }

  // ==================== NEW: TEMPLATE ENDPOINTS ====================

  async getActionTemplates() {
    const response = await this.fetchWithAuth("/employee-actions/templates");
    return Array.isArray(response) ? response : response.data || [];
  }

  async createActionTemplate(templateData) {
    const response = await this.fetchWithAuth("/employee-actions/templates", {
      method: "POST",
      body: JSON.stringify(templateData),
    });
    return response.data || response;
  }

  // ==================== HELPER METHODS ====================

  /**
   * Get action type configuration
   */
  getActionTypeConfig(actionType) {
    const configs = {
      promote: {
        label: "Promote Employee",
        color: "#10b981",
        icon: "TrendingUp",
        requiresApproval: true,
        approvalLevels: 3,
      },
      demote: {
        label: "Demote Employee",
        color: "#ef4444",
        icon: "TrendingDown",
        requiresApproval: true,
        approvalLevels: 3,
      },
      transfer: {
        label: "Transfer Employee",
        color: "#3b82f6",
        icon: "MapPin",
        requiresApproval: true,
        approvalLevels: 3,
      },
      salary_change: {
        label: "Salary Adjustment",
        color: "#f59e0b",
        icon: "DollarSign",
        requiresApproval: true,
        approvalLevels: 3,
      },
      terminate: {
        label: "Terminate Employment",
        color: "#dc2626",
        icon: "UserX",
        requiresApproval: true,
        approvalLevels: 4,
      },
      suspend: {
        label: "Suspend Employee",
        color: "#f97316",
        icon: "UserMinus",
        requiresApproval: true,
        approvalLevels: 2,
      },
      rehire: {
        label: "Rehire Employee",
        color: "#8b5cf6",
        icon: "RefreshCw",
        requiresApproval: true,
        approvalLevels: 3,
      },
      contract: {
        label: "Contract Update",
        color: "#06b6d4",
        icon: "FileCheck",
        requiresApproval: true,
        approvalLevels: 3,
      },
      rotation: {
        label: "Job Rotation",
        color: "#ec4899",
        icon: "Repeat",
        requiresApproval: true,
        approvalLevels: 3,
      },
      review: {
        label: "Performance Review",
        color: "#14b8a6",
        icon: "ClipboardList",
        requiresApproval: false,
        approvalLevels: 1,
      },
      training: {
        label: "Training Assignment",
        color: "#6366f1",
        icon: "GraduationCap",
        requiresApproval: false,
        approvalLevels: 2,
      },
    };

    return configs[actionType] || null;
  }

  /**
   * Get status badge configuration
   */
  getStatusConfig(status) {
    const configs = {
      pending: { label: "Pending", color: "#f59e0b", bgColor: "#fef3c7" },
      approved: { label: "Approved", color: "#10b981", bgColor: "#dcfce7" },
      rejected: { label: "Rejected", color: "#ef4444", bgColor: "#fee2e2" },
      completed: { label: "Completed", color: "#3b82f6", bgColor: "#dbeafe" },
    };

    return (
      configs[status] || { label: status, color: "#64748b", bgColor: "#f1f5f9" }
    );
  }
}

// Create singleton instance
const employeeActionsAPI = new EmployeeActionsAPI();

export default employeeActionsAPI;